import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Container, Message, Progress, Button, Icon, Segment, Loader } from 'semantic-ui-react';

import Config from '../../constants/Config';
import { Markdown } from '../../lib/custom-ui';
import SignaturePad from '../SignaturePad';
import CameraCapture from '../CameraCapture';
import styles from './PublicInspectionForm.module.scss';

const isMobileDevice = () =>
  /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.innerWidth <= 767;

const PublicInspectionForm = () => {
  const isMobile = isMobileDevice();
  const { token } = useParams();
  const [formData, setFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [photoPreviews, setPhotoPreviews] = useState({});
  const [signatures, setSignatures] = useState({});
  const [validationMessage, setValidationMessage] = useState(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  const [uploadedPhotoUrls, setUploadedPhotoUrls] = useState({});
  const contentRef = React.useRef(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm();

  // Load form data using public token
  useEffect(() => {
    const loadForm = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${Config.SERVER_BASE_URL}/api/public/inspection/${token}`);

        if (response.ok) {
          const data = await response.json();

          if (data.alreadyUsed) {
            setError('This inspection has already been completed');
            setFormData(null);
          } else {
            setFormData(data);
          }
        } else {
          setError('Invalid or expired link');
        }
      } catch (err) {
        setError('Error loading inspection form');
        console.error('Error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    if (token) {
      loadForm();
    }
  }, [token]);

  const watchedValues = watch();

  // Restore draft if one exists (protects against data loss)
  useEffect(() => {
    let mounted = true;
    const loadDraft = async () => {
      try {
        const resp = await fetch(`${Config.SERVER_BASE_URL}/api/public/inspection/${token}/draft`);
        if (resp.ok) {
          const data = await resp.json();
          if (mounted && data.item) {
            const draft = data.item;

            if (draft.draftData && typeof draft.draftData === 'object') {
              Object.entries(draft.draftData).forEach(([key, val]) => {
                setValue(key, val, { shouldDirty: false });
              });
            }

            if (draft.uploadedPhotos && typeof draft.uploadedPhotos === 'object') {
              setUploadedPhotoUrls(draft.uploadedPhotos);
              const restoredPreviews = {};
              Object.entries(draft.uploadedPhotos).forEach(([fieldName, urls]) => {
                if (Array.isArray(urls) && urls.length > 0) {
                  restoredPreviews[fieldName] = urls;
                }
              });
              if (Object.keys(restoredPreviews).length > 0) {
                setPhotoPreviews(restoredPreviews);
              }
            }

            if (draft.signatureData && typeof draft.signatureData === 'object') {
              setSignatures(draft.signatureData);
            }

            if (typeof draft.currentSection === 'number' && draft.currentSection > 0) {
              setCurrentSection(draft.currentSection);
            }

            setDraftRestored(true);
            console.log(
              '📋 Public draft restored — resuming from section',
              draft.currentSection + 1
            );
          }
        }
      } catch (e) {
        // Draft load is non-critical
      }
    };
    if (token) {
      loadDraft();
    }
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, setValue]);

  // Load defaults from backend (for card_extraction fields)
  useEffect(() => {
    if (formData?.defaults && Object.keys(formData.defaults).length > 0) {
      Object.entries(formData.defaults).forEach(([key, value]) => {
        setValue(key, value, { shouldDirty: false });
      });
    }
  }, [formData?.defaults, setValue]);

  // Resolve variable value from card context
  const resolveVariable = key => {
    if (!formData?.card || !key) return '';
    if (!key.startsWith('card.')) return '';
    const path = key.split('.').slice(1); // remove 'card'
    let cur = formData.card;
    for (const segment of path) {
      if (cur && Object.prototype.hasOwnProperty.call(cur, segment)) {
        cur = cur[segment];
      } else {
        return '';
      }
    }
    if (cur == null) return '';
    return String(cur);
  };

  // Prefill variable fields once formData is available
  useEffect(() => {
    if (!formData?.form?.formSchema?.sections) return;
    try {
      formData.form.formSchema.sections.forEach(sec => {
        (sec.fields || []).forEach(f => {
          if (f.type === 'variable') {
            const val = resolveVariable(f.variableKey || 'card.name');
            if (val) {
              setValue(f.name, val, { shouldDirty: false });
            }
          }
        });
      });
    } catch (_) {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  // Scroll to top when section changes
  useEffect(() => {
    // Scroll to top of page with smooth behavior
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Also scroll the content container if ref is available
    if (contentRef.current) {
      setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.scrollTop = 0;
        }
      }, 0);
    }
  }, [currentSection]);

  const handleFileChange = (fieldName, files) => {
    if (!files || files.length === 0) {
      return;
    }

    // ACUMULAR previews (no reemplazar)
    const fileArray = Array.from(files);
    const newPreviews = [];
    let loadedCount = 0;

    fileArray.forEach((file, index) => {
      const reader = new FileReader();
      reader.onload = e => {
        newPreviews[index] = e.target.result;
        loadedCount++;

        // Cuando todas las NUEVAS imágenes estén cargadas
        if (loadedCount === fileArray.length) {
          setPhotoPreviews(prev => {
            // Obtener previews anteriores de este campo
            const existingPreviews = prev[fieldName] || [];

            // AGREGAR las nuevas al final
            return {
              ...prev,
              [fieldName]: [...existingPreviews, ...newPreviews.filter(p => p)],
            };
          });
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (fieldName, index) => {
    setPhotoPreviews(prev => ({
      ...prev,
      [fieldName]: (prev[fieldName] || []).filter((_, i) => i !== index),
    }));
  };

  if (isLoading) {
    return (
      <Container className={styles.container}>
        <Segment className={styles.loadingSegment}>
          <Loader active inline="centered" size="large" />
          <p>Loading inspection form...</p>
        </Segment>
      </Container>
    );
  }

  if (error || !formData) {
    return (
      <Container className={styles.container}>
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error || 'Invalid inspection link'}</p>
        </Message>
      </Container>
    );
  }

  if (isSubmitted) {
    return (
      <Container className={styles.container}>
        <div className={styles.successScreen}>
          <Icon name="check circle" size="massive" color="green" />
          <h1>Inspection Completed!</h1>
          <p>
            We will analyze the response. This process will take 1-2 minutes. You can safely close
            this page.
          </p>
          <Button
            primary
            size="large"
            icon
            labelPosition="left"
            onClick={() => window.close()}
            className={styles.closeWindowBtn}
          >
            <Icon name="close" />
            Close Window
          </Button>
          <p className={styles.successHint}>
            If the window does not close, you can safely close this tab manually.
          </p>
        </div>
      </Container>
    );
  }

  const form = formData.form;
  const formSchema = form.formSchema;
  const sections = formSchema.sections || [];
  const totalSections = sections.length;
  const progress = ((currentSection + 1) / totalSections) * 100;
  const currentSectionData = sections[currentSection];

  const handleNext = async () => {
    setValidationMessage(null);

    // Get required field names from current section (excluding static fields)
    const currentFields = currentSectionData.fields || [];
    const requiredFieldNames = currentFields
      .filter(f => f.required && f.type !== 'static_text' && f.type !== 'link')
      .map(f => f.name);

    // Validate only current section fields
    const isValid = await trigger(requiredFieldNames);

    // Additional validation for camera/file fields (not handled by react-hook-form)
    const requiredCameraFields = currentFields.filter(
      f => f.required && (f.type === 'camera' || f.type === 'file')
    );
    const missingPhotos = requiredCameraFields.filter(
      f => !photoPreviews[f.name] || photoPreviews[f.name].length === 0
    );

    if (!isValid || missingPhotos.length > 0) {
      // Get names of invalid fields for user message
      const invalidFields = requiredFieldNames.filter(name => errors[name]);
      const fieldLabels = currentFields
        .filter(f => invalidFields.includes(f.name))
        .map(f => f.label);

      // Add missing photo fields to the message
      const missingPhotoLabels = missingPhotos.map(f => f.label);
      const allMissingLabels = [...fieldLabels, ...missingPhotoLabels];

      setValidationMessage(
        `Please complete the following required fields to continue: ${allMissingLabels.join(', ')}`
      );
      return;
    }

    if (currentSection < totalSections - 1) {
      const nextSection = currentSection + 1;
      setCurrentSection(nextSection);

      saveDraft(nextSection);
    }
  };

  const saveDraft = async sectionToSave => {
    setIsSavingDraft(true);
    try {
      const currentFields = sections[sectionToSave - 1]?.fields || [];
      const photoFields = currentFields.filter(f => f.type === 'camera' || f.type === 'file');

      const newUploaded = { ...uploadedPhotoUrls };

      for (const field of photoFields) {
        const previews = photoPreviews[field.name];
        if (!Array.isArray(previews) || previews.length === 0) continue;

        const urls = [];
        for (const photo of previews) {
          const isBase64 =
            typeof photo === 'string' &&
            (photo.startsWith('data:image/') || photo.includes(';base64,'));

          if (isBase64) {
            try {
              const uploadResp = await fetch(
                `${Config.SERVER_BASE_URL}/api/public/inspection/${token}/upload-image`,
                {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ imageData: photo }),
                }
              );
              if (uploadResp.ok) {
                const result = await uploadResp.json();
                urls.push(result.url);
              } else {
                urls.push(photo);
              }
            } catch (e) {
              urls.push(photo);
            }
          } else {
            urls.push(photo);
          }
        }

        newUploaded[field.name] = urls;
        setPhotoPreviews(prev => ({ ...prev, [field.name]: urls }));
      }

      setUploadedPhotoUrls(newUploaded);

      const allValues = watch();

      await fetch(`${Config.SERVER_BASE_URL}/api/public/inspection/${token}/draft`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          draftData: allValues,
          uploadedPhotos: newUploaded,
          signatureData: signatures,
          currentSection: sectionToSave,
        }),
      });
      console.log('💾 Public draft saved — section', sectionToSave);
    } catch (e) {
      console.warn('Public draft save failed (non-critical):', e);
    } finally {
      setIsSavingDraft(false);
    }
  };

  const deleteDraft = async () => {
    // Public drafts are cleaned up when token is marked as used on submit
    // No explicit delete endpoint needed for public forms
  };

  const handlePrevious = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  // Upload photos to Firebase Storage using PUBLIC endpoint
  const uploadPhotosToFirebase = async photoData => {
    const uploadedPhotos = {};

    for (const [fieldName, photos] of Object.entries(photoData)) {
      if (!Array.isArray(photos) || photos.length === 0) {
        continue;
      }

      const uploadedUrls = [];

      for (const photo of photos) {
        console.log(
          '📸 Processing photo for upload:',
          typeof photo,
          typeof photo === 'string' ? photo.substring(0, 50) + '...' : 'not-a-string'
        );

        // Check más robusto para base64
        const isBase64 =
          typeof photo === 'string' &&
          (photo.startsWith('data:image/') || photo.includes(';base64,'));

        if (isBase64) {
          try {
            const uploadResponse = await fetch(
              `${Config.SERVER_BASE_URL}/api/public/inspection/${token}/upload-image`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                // No credentials/auth headers needed for public endpoint
                body: JSON.stringify({
                  imageData: photo,
                }),
              }
            );

            if (uploadResponse.ok) {
              const result = await uploadResponse.json();
              uploadedUrls.push(result.url);
              console.log('✅ Public Image uploaded:', result.url);
            } else {
              const errorText = await uploadResponse.text();
              console.error('Failed to upload image. Status:', uploadResponse.status, errorText);
              // alert(`Error uploading image. Please try smaller files.`);
            }
          } catch (error) {
            console.error('Error uploading image:', error);
          }
        } else {
          // No es base64, mantenerlo como está (asumir que ya es URL o algo válido)
          uploadedUrls.push(photo);
        }
      }

      uploadedPhotos[fieldName] = uploadedUrls;
    }

    return uploadedPhotos;
  };

  const onSubmit = async data => {
    if (currentSection !== totalSections - 1) {
      return;
    }

    // Validate camera/file fields before submitting
    const currentFields = currentSectionData.fields || [];
    const requiredCameraFields = currentFields.filter(
      f => f.required && (f.type === 'camera' || f.type === 'file')
    );
    const missingPhotos = requiredCameraFields.filter(
      f => !photoPreviews[f.name] || photoPreviews[f.name].length === 0
    );

    if (missingPhotos.length > 0) {
      const missingPhotoLabels = missingPhotos.map(f => f.label);
      setValidationMessage(
        `Please complete the following required fields before submitting: ${missingPhotoLabels.join(
          ', '
        )}`
      );
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // 1. Subir fotos a Firebase Storage primero (Unificando lógica con FillInspectionModal)
      console.log('📤 Uploading public photos to Firebase Storage...');
      const uploadedPhotos = await uploadPhotosToFirebase(photoPreviews);

      // 2. Combinar datos del form con firmas Y fotos ya subidas
      const completeData = {
        ...data,
        ...signatures, // Agregar firmas al response data
        ...uploadedPhotos, // Agregar URLs de fotos (ya no base64)
      };

      const payload = {
        token: token,
        responseData: completeData,
        submittedByName: data.inspectorName || 'External user',
      };

      // Solo agregar signatureData si existe (igual que FillInspectionModal)
      const signature = signatures.inspectorSignature || signatures.customerSignature;
      if (signature) {
        payload.signatureData = signature;
      }

      console.log('📝 Submitting public inspection:', payload);

      const response = await fetch(
        `${Config.SERVER_BASE_URL}/api/public/inspection/${token}/submit`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to submit inspection');
      }
    } catch (err) {
      setError('Error submitting inspection');
      console.error('Error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reuse same field rendering logic as FillInspectionModal
  const renderField = field => {
    // Check conditional logic
    if (field.conditional) {
      const conditionField = field.conditional.field;
      const conditionValue = field.conditional.value;
      const conditionOperator = field.conditional.operator;
      const currentValue = watchedValues[conditionField];

      let showField = false;
      switch (conditionOperator) {
        case '===':
          showField = currentValue === conditionValue;
          break;
        case '!==':
          showField = currentValue !== conditionValue;
          break;
        default:
          showField = true;
      }

      if (!showField) {
        return null;
      }
    }

    const fieldKey = field.id || field.name;

    switch (field.type) {
      case 'text':
      case 'number':
        return (
          <div key={fieldKey} className={styles.field}>
            <label>
              {field.label}
              {field.required && <span className={styles.required}>*</span>}
            </label>
            <input
              type={field.type}
              {...register(field.name, { required: field.required })}
              placeholder={field.label}
            />
            {errors[field.name] && <span className={styles.error}>This field is required</span>}
          </div>
        );

      case 'textarea':
        return (
          <div key={fieldKey} className={styles.field}>
            <label>
              {field.label}
              {field.required && <span className={styles.required}>*</span>}
            </label>
            <textarea
              {...register(field.name, { required: field.required })}
              rows={field.rows || 3}
              placeholder={field.label}
            />
            {errors[field.name] && <span className={styles.error}>This field is required</span>}
          </div>
        );

      case 'select':
        return (
          <div key={fieldKey} className={styles.field}>
            <label>
              {field.label}
              {field.required && <span className={styles.required}>*</span>}
            </label>
            <select {...register(field.name, { required: field.required })}>
              <option value="">-- Select --</option>
              {field.options?.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errors[field.name] && <span className={styles.error}>This field is required</span>}
          </div>
        );

      case 'radio':
        return (
          <div key={fieldKey} className={styles.field}>
            <label>
              {field.label}
              {field.required && <span className={styles.required}>*</span>}
            </label>
            <div className={styles.radioGroup}>
              {field.options?.map(option => (
                <label key={option} className={styles.radioOption}>
                  <input
                    type="radio"
                    value={option}
                    {...register(field.name, { required: field.required })}
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
            {errors[field.name] && <span className={styles.error}>This field is required</span>}
          </div>
        );

      case 'checkbox':
        return (
          <div key={fieldKey} className={styles.field}>
            <label>
              {field.label}
              {field.required && <span className={styles.required}>*</span>}
            </label>
            <div className={styles.checkboxGroup}>
              {field.options?.map(option => (
                <label key={option} className={styles.checkboxOption}>
                  <input type="checkbox" value={option} {...register(field.name)} />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 'date':
        return (
          <div key={fieldKey} className={styles.field}>
            <label>
              {field.label}
              {field.required && <span className={styles.required}>*</span>}
            </label>
            <input type="date" {...register(field.name, { required: field.required })} />
            {errors[field.name] && <span className={styles.error}>This field is required</span>}
          </div>
        );

      case 'time':
        return (
          <div key={fieldKey} className={styles.field}>
            <label>
              {field.label}
              {field.required && <span className={styles.required}>*</span>}
            </label>
            <input type="time" {...register(field.name, { required: field.required })} />
            {errors[field.name] && <span className={styles.error}>This field is required</span>}
          </div>
        );

      case 'camera':
      case 'file': {
        const hasPhotos = photoPreviews[field.name]?.length > 0;
        const showError =
          field.required && !hasPhotos && (validationMessage || Object.keys(errors).length > 0);
        const isCamera = field.type === 'camera';

        return (
          <div key={fieldKey} className={styles.field}>
            <label>
              {field.label}
              {field.required && <span className={styles.required}>*</span>}
            </label>

            {isCamera ? (
              isMobile ? (
                <label className={styles.nativeCameraBtn}>
                  <Icon name="camera" />
                  <span>{hasPhotos ? 'Take Another Photo' : 'Take Photo'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={e => {
                      handleFileChange(field.name, e.target.files);
                      if (validationMessage) setValidationMessage(null);
                    }}
                  />
                </label>
              ) : (
                <CameraCapture
                  onCapture={photos => {
                    setPhotoPreviews(prev => {
                      const existing = prev[field.name] || [];
                      return {
                        ...prev,
                        [field.name]: [...existing, ...photos],
                      };
                    });
                    if (validationMessage) setValidationMessage(null);
                  }}
                  multiple={field.multiple}
                  fieldLabel={field.label}
                />
              )
            ) : (
              <div className={styles.fileUpload}>
                <Icon name="file" size="large" />
                <input
                  type="file"
                  accept={field.accept || 'image/*'}
                  multiple
                  onChange={e => {
                    handleFileChange(field.name, e.target.files);
                    if (validationMessage) setValidationMessage(null);
                  }}
                />
                <p>{field.photoStage || 'Click to upload'}</p>
              </div>
            )}

            {hasPhotos && (
              <div className={styles.photoPreviewContainer}>
                {photoPreviews[field.name].map((preview, idx) => (
                  <div key={idx} className={styles.photoPreview}>
                    <img src={preview} alt={`Preview ${idx + 1}`} />
                    <Button
                      circular
                      icon="close"
                      size="mini"
                      color="red"
                      className={styles.removeBtn}
                      onClick={() => removePhoto(field.name, idx)}
                    />
                  </div>
                ))}
              </div>
            )}

            {showError && <span className={styles.error}>At least one photo is required</span>}
          </div>
        );
      }

      case 'signature':
        return (
          <div key={fieldKey} className={styles.field}>
            <label>
              {field.label}
              {field.required && <span className={styles.required}>*</span>}
            </label>
            <SignaturePad
              onChange={dataUrl => {
                setSignatures(prev => ({
                  ...prev,
                  [field.name]: dataUrl,
                }));
              }}
              required={field.required}
            />
            {field.required && !signatures[field.name] && errors.submit && (
              <span className={styles.error}>Signature is required</span>
            )}
          </div>
        );

      case 'static_text':
        return (
          <div
            key={fieldKey}
            className={styles.staticText}
            dangerouslySetInnerHTML={{ __html: field.formattedText || field.label }}
          />
        );

      case 'link': {
        const url = field.url || field.label;
        const idMatch = url && url.match(/\/d\/([-\w]+)/);
        const fileId = idMatch ? idMatch[1] : null;
        const isGoogleDrive = url && url.includes('drive.google.com');

        return (
          <div key={fieldKey} className={styles.field}>
            <div className={styles.linkContainer}>
              <Icon name="linkify" />
              <a href={url} target="_blank" rel="noopener noreferrer">
                {field.label || field.url}
              </a>
            </div>
            {isGoogleDrive && fileId && (
              <div className={styles.linkImagePreview}>
                <img
                  src={`https://drive.google.com/thumbnail?id=${fileId}&sz=w640`}
                  alt="Preview"
                  referrerPolicy="no-referrer"
                  style={{
                    width: '300px',
                    height: 'auto',
                    borderRadius: '8px',
                    border: '1px solid #ccc',
                    marginTop: '10px',
                    display: 'block',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                />
              </div>
            )}
          </div>
        );
      }

      case 'card_extraction': {
        const extractedValue = watchedValues[field.name] || '';
        return (
          <div key={fieldKey} className={styles.field}>
            <label>
              {field.label}
              {field.required && <span className={styles.required}>*</span>}
            </label>
            {/* Hidden input for form submission */}
            <input type="hidden" {...register(field.name, { required: field.required })} />
            {/* Markdown formatted preview */}
            <div
              className={styles.cardExtractionPreview}
              style={{
                padding: '12px',
                backgroundColor: '#f8f9fa',
                borderRadius: '6px',
                border: '1px solid #dee2e6',
                minHeight: '60px',
                maxHeight: '300px',
                overflowY: 'auto',
              }}
            >
              {extractedValue ? (
                <Markdown linkStopPropagation linkTarget="_blank">
                  {extractedValue}
                </Markdown>
              ) : (
                <span style={{ color: '#6c757d', fontStyle: 'italic' }}>
                  {field.placeholder || 'No data extracted'}
                </span>
              )}
            </div>
            {errors[field.name] && <span className={styles.error}>This field is required</span>}
          </div>
        );
      }

      case 'variable': {
        const value =
          watchedValues[field.name] || resolveVariable(field.variableKey || 'card.name');
        return (
          <div key={fieldKey} className={styles.field}>
            <label>
              {field.label || field.variableLabel || 'Variable'}
              {field.required && <span className={styles.required}>*</span>}
            </label>
            <input
              type="text"
              {...register(field.name, { required: field.required })}
              defaultValue={value}
              readOnly={!!field.readOnly}
              placeholder={field.placeholder || field.variableLabel || ''}
            />
            {errors[field.name] && <span className={styles.error}>This field is required</span>}
          </div>
        );
      }

      case 'wheelbase_vehicle':
      case 'wheelbase_booking':
        // Wheelbase integration requires authentication, not available in public forms
        // Render as a regular text input instead
        return (
          <div key={fieldKey} className={styles.field}>
            <label>
              {field.label}
              {field.required && <span className={styles.required}>*</span>}
            </label>
            <input
              type="text"
              {...register(field.name, { required: field.required })}
              placeholder={field.placeholder || field.label}
            />
            <small style={{ color: '#6c757d', fontStyle: 'italic' }}>
              Note: Search functionality not available in public forms
            </small>
            {errors[field.name] && <span className={styles.error}>This field is required</span>}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Container className={styles.container}>
      <div className={styles.header}>
        <Icon name="clipboard check" size="huge" color="blue" />
        <h1>{formSchema.title || form.name}</h1>
        <p>{formSchema.description}</p>
      </div>

      {/* Progress bar */}
      <div className={styles.progressContainer}>
        <div className={styles.progressInfo}>
          <span>
            Section {currentSection + 1} of {totalSections}
            {isSavingDraft && (
              <Icon name="cloud upload" size="small" style={{ marginLeft: 6, opacity: 0.6 }} />
            )}
            {draftRestored && currentSection === 0 && !isSavingDraft && (
              <span style={{ marginLeft: 8, fontSize: '0.85em', color: '#2185d0' }}>
                <Icon name="redo" size="small" /> Resumed from draft
              </span>
            )}
          </span>
          <span>{Math.round(progress)}% complete</span>
        </div>
        <Progress percent={progress} color="blue" />
      </div>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      <Segment className={styles.formSegment} ref={contentRef}>
        <form
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
        >
          <div className={styles.sectionHeader}>
            <h2>{currentSectionData.title}</h2>
          </div>

          <div className={styles.fieldsContainer}>
            {currentSectionData.fields
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map(field => renderField(field))}
          </div>

          {/* Validation message */}
          {validationMessage && (
            <Message warning>
              <Icon name="warning sign" />
              {validationMessage}
            </Message>
          )}

          {/* Navigation */}
          <div className={styles.navigation}>
            <Button
              type="button"
              onClick={handlePrevious}
              disabled={currentSection === 0}
              size="large"
            >
              <Icon name="chevron left" />
              Previous
            </Button>

            {currentSection === totalSections - 1 ? (
              <Button
                type="button"
                primary
                loading={isSubmitting}
                disabled={isSubmitting}
                size="large"
                onClick={handleSubmit(onSubmit)}
              >
                Submit Inspection
                <Icon name="check" />
              </Button>
            ) : (
              <Button type="button" primary onClick={handleNext} size="large">
                Next
                <Icon name="chevron right" />
              </Button>
            )}
          </div>
        </form>
      </Segment>

      <div className={styles.footer}>
        <p>Powered by Shakkii</p>
      </div>
    </Container>
  );
};

export default PublicInspectionForm;
